/**
 * Utilitaires pour les calculs de semaines et l'alternance semaine A/B
 *
 * Ce fichier fournit des fonctions pour :
 * - Calculer le numéro de semaine ISO
 * - Déterminer si une semaine est A (impaire) ou B (paire)
 * - Calculer la différence en semaines entre deux dates
 */

/**
 * Calcule le numéro de semaine ISO pour une date donnée
 * Selon la norme ISO 8601 :
 * - La semaine 1 est celle qui contient le premier jeudi de l'année
 * - Les semaines commencent le lundi
 *
 * @param {Date} date - Date à analyser
 * @returns {number} Numéro de semaine ISO (1-53)
 */
export function getISOWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

/**
 * Obtient l'année ISO pour une date donnée
 * (peut différer de l'année calendaire pour les dates en début/fin d'année)
 *
 * @param {Date} date - Date à analyser
 * @returns {number} Année ISO
 */
export function getISOWeekYear(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  return d.getUTCFullYear();
}

/**
 * Détermine si une semaine est de type A (impaire) ou B (paire)
 * Par rapport à une date de référence
 *
 * La semaine de la date de référence est toujours 'A'
 * Les semaines suivantes alternent : A, B, A, B, etc.
 *
 * @param {Date} date - Date à analyser
 * @param {Date} referenceDate - Date de référence (début de période)
 * @returns {string} 'A' ou 'B'
 */
export function getWeekType(date, referenceDate) {
  const weeksDiff = getWeeksDifference(referenceDate, date);
  return weeksDiff % 2 === 0 ? 'A' : 'B';
}

/**
 * Calcule la différence en semaines entre deux dates
 *
 * @param {Date} startDate - Date de début
 * @param {Date} endDate - Date de fin
 * @returns {number} Nombre de semaines (peut être négatif si endDate < startDate)
 */
export function getWeeksDifference(startDate, endDate) {
  // Normaliser à minuit
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  // Calculer au lundi de la semaine pour chaque date
  const startMonday = getMondayOfWeek(start);
  const endMonday = getMondayOfWeek(end);

  // Différence en millisecondes / millisecondes par semaine
  const diff = endMonday - startMonday;
  return Math.floor(diff / (7 * 24 * 60 * 60 * 1000));
}

/**
 * Obtient le lundi de la semaine pour une date donnée
 *
 * @param {Date} date - Date à analyser
 * @returns {Date} Lundi de la semaine
 */
export function getMondayOfWeek(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Ajuster si dimanche
  return new Date(d.setDate(diff));
}

/**
 * Obtient le dimanche de la semaine pour une date donnée
 *
 * @param {Date} date - Date à analyser
 * @returns {Date} Dimanche de la semaine
 */
export function getSundayOfWeek(date) {
  const monday = getMondayOfWeek(date);
  return new Date(monday.setDate(monday.getDate() + 6));
}

/**
 * Génère un objet représentant une semaine complète
 *
 * @param {Date} date - Date appartenant à la semaine
 * @returns {Object} { monday: Date, sunday: Date, weekNumber: number, year: number }
 */
export function getWeekInfo(date) {
  const monday = getMondayOfWeek(date);
  const sunday = getSundayOfWeek(date);
  const weekNumber = getISOWeekNumber(date);
  const year = getISOWeekYear(date);

  return {
    monday,
    sunday,
    weekNumber,
    year,
    label: `Semaine ${weekNumber} - ${year}`
  };
}

/**
 * Compte le nombre de semaines de type A et B dans une plage de dates
 *
 * @param {Date} startDate - Date de début
 * @param {Date} endDate - Date de fin
 * @param {Date} referenceDate - Date de référence pour déterminer A/B
 * @returns {Object} { totalWeeks: number, weekA: number, weekB: number }
 */
export function countWeekTypes(startDate, endDate, referenceDate) {
  const totalWeeks = Math.abs(getWeeksDifference(startDate, endDate)) + 1;

  let weekA = 0;
  let weekB = 0;

  let currentDate = new Date(startDate);
  for (let i = 0; i < totalWeeks; i++) {
    const weekType = getWeekType(currentDate, referenceDate);
    if (weekType === 'A') {
      weekA++;
    } else {
      weekB++;
    }
    currentDate = new Date(currentDate.setDate(currentDate.getDate() + 7));
  }

  return { totalWeeks, weekA, weekB };
}

/**
 * Génère toutes les dates d'un jour de la semaine spécifique
 * dans une plage de dates, avec filtre optionnel par type de semaine (A/B)
 *
 * @param {Date} startDate - Date de début
 * @param {Date} endDate - Date de fin
 * @param {number} dayOfWeek - Jour de la semaine (0=dimanche, 1=lundi, ..., 6=samedi)
 * @param {Date} referenceDate - Date de référence pour A/B (optionnel)
 * @param {string} weekTypeFilter - 'A', 'B' ou null pour toutes les semaines
 * @returns {Array<Date>} Liste des dates
 */
export function getDatesForWeekday(startDate, endDate, dayOfWeek, referenceDate = null, weekTypeFilter = null) {
  const dates = [];
  const current = new Date(startDate);
  current.setHours(0, 0, 0, 0);

  // Avancer jusqu'au premier jour de la semaine souhaité
  while (current.getDay() !== dayOfWeek && current <= endDate) {
    current.setDate(current.getDate() + 1);
  }

  // Collecter toutes les occurrences
  while (current <= endDate) {
    // Appliquer le filtre de type de semaine si spécifié
    if (weekTypeFilter && referenceDate) {
      const weekType = getWeekType(current, referenceDate);
      if (weekType === weekTypeFilter) {
        dates.push(new Date(current));
      }
    } else {
      dates.push(new Date(current));
    }

    // Passer à la semaine suivante
    current.setDate(current.getDate() + 7);
  }

  return dates;
}

/**
 * Formate une plage de dates en texte lisible
 *
 * @param {Date} startDate - Date de début
 * @param {Date} endDate - Date de fin
 * @returns {string} Plage formatée (ex: "1 janv. 2025 - 31 déc. 2025")
 */
export function formatDateRange(startDate, endDate) {
  const options = { day: 'numeric', month: 'short', year: 'numeric' };
  const start = startDate.toLocaleDateString('fr-FR', options);
  const end = endDate.toLocaleDateString('fr-FR', options);
  return `${start} - ${end}`;
}

/**
 * Vérifie si deux dates sont dans la même semaine ISO
 *
 * @param {Date} date1 - Première date
 * @param {Date} date2 - Deuxième date
 * @returns {boolean} true si même semaine
 */
export function isSameISOWeek(date1, date2) {
  const week1 = getISOWeekNumber(date1);
  const year1 = getISOWeekYear(date1);
  const week2 = getISOWeekNumber(date2);
  const year2 = getISOWeekYear(date2);

  return week1 === week2 && year1 === year2;
}

export default {
  getISOWeekNumber,
  getISOWeekYear,
  getWeekType,
  getWeeksDifference,
  getMondayOfWeek,
  getSundayOfWeek,
  getWeekInfo,
  countWeekTypes,
  getDatesForWeekday,
  formatDateRange,
  isSameISOWeek
};
